import React, { useState, useEffect, useRef, useCallback } from 'react';
import { chatService } from '../../services/chatService';
import ChatSidebar from './ChatSidebar';
import ChatPanel, { ChatPanelRef } from './ChatPanel';
import CitationModal from './CitationModal';

export interface Conversation {
  id: string;
  title: string;
  lastMessage?: string;
  timestamp?: Date;
}

interface CitationRef {
  id: string;
  file_path?: string;
  fund?: string;
  document_title?: string;
  as_of_date?: string;
  source_file_path?: string;
  source_file_name?: string;
}

const HISTORY_PAGE_SIZE = 30;

const QueryEnginePage: React.FC = () => {
  const chatPanelRef = useRef<ChatPanelRef>(null);
  const isLoadingConversationsRef = useRef(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>();
  const [historyPage, setHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [citationText, setCitationText] = useState('');
  const [citationTitle, setCitationTitle] = useState('');

  const extractAsOfDate = (source?: string) => {
    if (!source) return 'N/A';
    const dateMatch =
      source.match(/(\d{4}[-_]\d{2}[-_]\d{2})/) ||
      source.match(/(\d{2}[-_]\d{2}[-_]\d{4})/) ||
      source.match(/(\d{8})/);
    if (!dateMatch) return 'N/A';
    const raw = dateMatch[1];
    if (/^\d{8}$/.test(raw)) {
      return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
    }
    return raw.replaceAll('_', '-');
  };

  const buildCitationTitle = (citation: CitationRef) => {
    const docFromPath =
      citation.source_file_name ||
      citation.source_file_path?.split('/').pop() ||
      citation.file_path?.split('/').pop();
    const documentName =
      citation.document_title ||
      docFromPath?.replace(/\.[^.]+$/, '') ||
      'Unknown document';
    const asOfDate =
      citation.as_of_date ||
      extractAsOfDate(citation.source_file_name || citation.source_file_path || citation.file_path);
    const tagValue = citation.fund || 'N/A';
    return `${documentName} | As of: ${asOfDate} | Tag: ${tagValue}`;
  };

  const loadConversations = useCallback(async (page: number = 1, append: boolean = false) => {
    if (isLoadingConversationsRef.current) return;
    isLoadingConversationsRef.current = true;
    setIsLoadingMoreHistory(append);

    try {
      const response = await chatService.getChatHistory(page, HISTORY_PAGE_SIZE);
      const threads = response.data.items || [];
      const mapped = threads.map((thread: any) => ({
        id: thread.thread_id,
        title: thread.title || 'Untitled Chat',
        lastMessage: thread.title,
        timestamp: thread.created_at ? new Date(thread.created_at) : undefined,
      }));

      setConversations((prev) => {
        if (!append) return mapped;
        const existingIds = new Set(prev.map((conversation) => conversation.id));
        const nextItems = mapped.filter((conversation) => !existingIds.has(conversation.id));
        return [...prev, ...nextItems];
      });

      setHistoryPage(response.data.page || page);
      setHasMoreHistory((response.data.page || page) < (response.data.total_pages || page));
    } catch {
      if (!append) {
        setConversations([]);
      }
      setHasMoreHistory(false);
    } finally {
      isLoadingConversationsRef.current = false;
      setIsLoadingMoreHistory(false);
    }
  }, []);

  useEffect(() => {
    loadConversations(1, false);
  }, [loadConversations]);

  const handleLoadMoreConversations = useCallback(async () => {
    if (isLoadingConversationsRef.current || !hasMoreHistory) return;
    await loadConversations(historyPage + 1, true);
  }, [hasMoreHistory, historyPage, loadConversations]);

  const handleSelectConversation = async (convId: string) => {
    try {
      const response = await chatService.getThreadMessages(convId);
      if (response.data.conversations && chatPanelRef.current) {
        chatPanelRef.current.loadThread(convId, response.data.conversations);
        setCurrentConversationId(convId);
      }
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const handleNewChat = () => {
    if (chatPanelRef.current) {
      chatPanelRef.current.loadThread('', []);
      setCurrentConversationId(undefined);
    }
    loadConversations(1, false);
  };

  const handleDeleteConversation = async (convId: string) => {
    try {
      await chatService.deleteThread(convId);
      if (currentConversationId === convId && chatPanelRef.current) {
        chatPanelRef.current.loadThread('', []);
        setCurrentConversationId(undefined);
      }
      await loadConversations(1, false);
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  };

  const handleCitationClick = async (citation: CitationRef, page: number) => {
    // Open modal immediately with skeleton so the click feels responsive.
    setPdfUrl(null);
    setPdfPage(page);
    setCitationTitle(buildCitationTitle(citation));
    setPdfLoading(true);
    setShowPdfModal(true);

    try {
      let metadata = citation;
      try {
        const details = await chatService.getCitationDetails(citation.id);
        metadata = {
          id: details.id || citation.id,
          file_path: details.file_path || citation.file_path,
          fund: details.fund || citation.fund,
          document_title: details.document_title,
          as_of_date: details.as_of_date,
          source_file_path: details.source_file_path,
          source_file_name: details.source_file_name,
        };
        setCitationTitle(buildCitationTitle(metadata));
      } catch {
        // Fall back to citation object coming from chat response.
      }

      const blob = await chatService.getCitationPdf(citation.id);
      if (blob.type === 'application/json' || blob.size < 1000) {
        try {
          const text = await blob.text();
          const data = JSON.parse(text);
          setCitationText(data.text_content || 'No text content available');
          setShowPdfModal(false);
          setPdfLoading(false);
          setShowTextModal(true);
        } catch {
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
          setPdfPage(page);
          setPdfLoading(false);
        }
      } else {
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        setPdfPage(page);
        setPdfLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch citation:', err);
      setPdfLoading(false);
      setShowPdfModal(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#f9fafb]">
      <ChatSidebar
        conversations={conversations}
        currentConversationId={currentConversationId}
        collapsed={sidebarCollapsed}
        hasMoreConversations={hasMoreHistory}
        isLoadingMoreConversations={isLoadingMoreHistory}
        onToggleCollapse={() => setSidebarCollapsed((p) => !p)}
        onNewChat={handleNewChat}
        onLoadMoreConversations={handleLoadMoreConversations}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <ChatPanel
          ref={chatPanelRef}
          onCitationClick={handleCitationClick}
          onNewThread={loadConversations}
        />
      </div>

      <CitationModal
        type="pdf"
        visible={showPdfModal}
        loading={pdfLoading}
        pdfUrl={pdfUrl}
        pdfPage={pdfPage}
        title={citationTitle}
        onClose={() => {
          setShowPdfModal(false);
          setPdfLoading(false);
          if (pdfUrl) URL.revokeObjectURL(pdfUrl);
          setPdfUrl(null);
          setCitationTitle('');
        }}
      />

      <CitationModal
        type="text"
        visible={showTextModal}
        title={citationTitle}
        textContent={citationText}
        onClose={() => {
          setShowTextModal(false);
          setCitationText('');
          setCitationTitle('');
        }}
      />
    </div>
  );
};

export default QueryEnginePage;
